import { noop } from 'lodash';
import { useCallback, useState } from 'react';
import type { KeyOption, MatchSorterOptions } from 'match-sorter';
import type { ChangeEvent, FC, ReactNode } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import InfoIcon from '@mui/icons-material/Info';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';

import { EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useDebounce } from '../../hooks/useDebounce.hook';

import { Dialog } from '../Dialog';

import { FILTERABLE_CARD_DEFAULT_SIZE } from './constants/card.constant';
import { VirtualCardRows } from './VirtualCardRows';
import { filteredItemsOf } from './utils/filtered-items.util';
import {
  SEARCH_TOLERANCE_LABELS,
  SearchTolerance,
} from './utils/search-tolerance.util';
import type { CardSize } from './types/card-size.type';

interface IdableItem {
  readonly _id: Id;
}

interface Props<I extends IdableItem> {
  readonly Card: FC<Readonly<{ item: I }>>;
  readonly createNewCard?: ReactNode;
  readonly id: string;
  readonly items: readonly I[];
  readonly keys: readonly Readonly<{
    key: KeyOption<I>;
    explanation: string;
    // How exactly it matches (a function key only beyond Exact), Exact by default.
    tolerance?: SearchTolerance;
  }>[];
  readonly options?: Omit<MatchSorterOptions<I>, 'keys'>;
  readonly searchBarPlaceholder?: string;
  readonly settings?: Readonly<{
    size?: CardSize;
  }>;
  readonly showSearchBar?: boolean;
}

const DEBOUNCE_DELAY = 500;

const defaultSettings = {
  size: FILTERABLE_CARD_DEFAULT_SIZE,
};

export const FilterableCards = <I extends IdableItem>({
  Card,
  createNewCard,
  id,
  items,
  keys,
  options,
  searchBarPlaceholder,
  settings = defaultSettings,
  showSearchBar = true,
}: Readonly<Props<I>>) => {
  const [isInfoDialogVisible, setIsInfoDialogVisible] = useState(false);
  const showInfoDialog = useCallback(() => {
    setIsInfoDialogVisible(true);
  }, []);

  const [searchBarValue, setSearchBarValue] = useState(EMPTY_STRING);
  const handleSearchBarValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchBarValue(event.target.value);
    },
    [],
  );
  const clearSearchBarValue = useCallback(() => {
    setSearchBarValue(EMPTY_STRING);
  }, []);
  const debouncedSearchBarValue = useDebounce(searchBarValue, DEBOUNCE_DELAY);

  const filteredItems = filteredItemsOf({
    items,
    search: debouncedSearchBarValue,
    keys: keys.map(({ key }) => key),
    tolerantKeys: keys.flatMap(({ key, tolerance }) =>
      tolerance &&
      tolerance !== SearchTolerance.Exact &&
      typeof key === 'function'
        ? [{ key, tolerance }]
        : [],
    ),
    options,
  });

  const size = settings.size ?? FILTERABLE_CARD_DEFAULT_SIZE;
  const cards = [
    ...filteredItems.map((item) => (
      <Grid key={item._id} size={size}>
        <Card item={item} />
      </Grid>
    )),
    ...(createNewCard
      ? [
          <Grid key="createNewCard" size={size}>
            {createNewCard}
          </Grid>,
        ]
      : []),
  ];

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!showSearchBar && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            id={`${id}-search-bar`}
            label="Rechercher"
            name="searchBar"
            onChange={handleSearchBarValueChange}
            placeholder={searchBarPlaceholder}
            slotProps={{
              input: {
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Informations sur les filtres"
                        edge="end"
                        onClick={showInfoDialog}
                      >
                        <InfoIcon />
                      </IconButton>
                    </InputAdornment>
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          debouncedSearchBarValue
                            ? 'Effacer la recherche'
                            : 'Rechercher'
                        }
                        disabled={!debouncedSearchBarValue}
                        edge="end"
                        onClick={
                          debouncedSearchBarValue ? clearSearchBarValue : noop
                        }
                      >
                        {debouncedSearchBarValue ? (
                          <SearchOffIcon />
                        ) : (
                          <SearchIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              },
            }}
            type="text"
            value={searchBarValue}
            variant="outlined"
          />
        </Box>
      )}
      <VirtualCardRows cards={cards} size={size} />
      <Dialog
        cancelLabel="J'ai compris"
        content={
          <List dense>
            {keys.map(({ explanation, tolerance }, index) => (
              <ListItemText
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                primary={explanation}
                secondary={
                  SEARCH_TOLERANCE_LABELS[tolerance ?? SearchTolerance.Exact]
                }
              />
            ))}
          </List>
        }
        id="folder-search-bar-info-dialog"
        isVisible={isInfoDialogVisible}
        setIsVisible={setIsInfoDialogVisible}
        showConfirm={false}
        title="Critères de recherche disponibles"
      />
    </>
  );
};
